import { useMutation } from "@tanstack/react-query";
import { roleNames } from "@/shared/identity/roles";
import { useState } from "react";

import { Button } from "@/design-system/Button";
import { Panel } from "@/design-system/Panel";
import { createInvitation, type InvitationResponse } from "@/shared/api";

type Props = {
  readonly login: string;
  readonly accountId: string;
  readonly roles: readonly string[];
};

export function AccessPanel({ login, accountId, roles }: Props): React.JSX.Element {
  const [invitee, setInvitee] = useState("");
  const [role, setRole] = useState<"OPERATOR" | "VIEWER">("VIEWER");
  const [password, setPassword] = useState("");
  const mutation = useMutation({
    mutationFn: async (): Promise<InvitationResponse> => {
      const { data, error } = await createInvitation({
        body: { login: invitee, roles: [role], current_password: password },
      });
      if (error !== undefined) {
        throw new Error("invitation refused");
      }
      return data;
    },
  });
  const invitation = mutation.data;
  const invitationUrl =
    invitation === undefined
      ? null
      : `${window.location.origin}/invite#token=${encodeURIComponent(invitation.token)}`;

  return (
    <Panel title="Доступ к счёту" testId="account-access">
      <div className="flex flex-col gap-3">
        <p className="text-body-main text-text-secondary">
          <span className="text-text-primary">{accountId}</span> · {login} ·{" "}
          {roleNames(roles)}
        </p>
        {!roles.includes("OWNER") ? (
          <p className="text-helper-sm text-text-muted">
            Приглашать пользователей может только владелец счёта.
          </p>
        ) : (
          <form
            className="grid grid-cols-1 gap-3 md:grid-cols-2"
            onSubmit={(event) => {
              event.preventDefault();
              if (!mutation.isPending && invitee !== "" && password !== "") {
                mutation.mutate();
              }
            }}
          >
            <label className="text-sm text-text-secondary">
              Логин приглашаемого
              <input
                className="mt-1 block w-full rounded-md border border-border-subtle bg-background-base px-3 py-2 text-text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring"
                type="text"
                // Not a sign-in form: the browser must not fill in the
                // owner's own login here (owner, 2026-10-30).
                name="invitee-login"
                autoComplete="off"
                data-1p-ignore
                data-lpignore="true"
                placeholder="например, ivan"
                required
                value={invitee}
                onChange={(event) => { setInvitee(event.target.value); }}
              />
            </label>
            <label className="text-sm text-text-secondary">
              Роль
              <select
                className="mt-1 block w-full rounded-md border border-border-subtle bg-background-base px-3 py-2 text-text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring"
                value={role}
                onChange={(event) => {
                  setRole(event.target.value === "OPERATOR" ? "OPERATOR" : "VIEWER");
                }}
              >
                <option value="VIEWER">Наблюдатель</option>
                <option value="OPERATOR">Оператор</option>
              </select>
            </label>
            <label className="text-sm text-text-secondary md:col-span-2">
              Текущий пароль владельца
              <input
                className="mt-1 block w-full rounded-md border border-border-subtle bg-background-base px-3 py-2 text-text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring"
                type="password"
                // A confirmation, not a sign-in: a saved password is not offered
                // and the field above is not taken for its login.
                name="owner-confirmation"
                autoComplete="new-password"
                required
                value={password}
                onChange={(event) => { setPassword(event.target.value); }}
              />
            </label>
            <div className="md:col-span-2">
              <Button
                type="submit"
                variant="primary"
                disabled={mutation.isPending || invitee === "" || password === ""}
              >
                Создать приглашение
              </Button>
            </div>
          </form>
        )}
        {mutation.isError && (
          <p role="alert" className="text-sm text-status-negative">
            Приглашение не создано. Проверьте пароль и права.
          </p>
        )}
        {invitation !== undefined && invitationUrl !== null && (
          <label className="text-sm text-text-secondary">
            Одноразовая ссылка, действует до {new Date(invitation.expires_at).toLocaleString("ru-RU")}
            <input
              aria-label="Ссылка приглашения"
              className="mt-1 block w-full rounded-md border border-border-subtle bg-background-base px-3 py-2 text-text-primary"
              readOnly
              value={invitationUrl}
            />
          </label>
        )}
      </div>
    </Panel>
  );
}
